import type { Map} from 'immutable';
import {List} from 'immutable';
import {assignmentExtensions} from 'resources/augmented/Assignment/assignment.extensions';

interface IdsToSelect {
  classroomId?: string;
  studentId?: string;
}

/** Subset of an assignment-relationship model reached from a classroom/student. */
interface AssignmentRelation {
  getSettings(): Map<string, unknown> | undefined;
}

/**
 * Subset of a classroom/student-classroom relationship model touched via
 * `getClassrooms()` / `getStudentClassrooms()` elements.
 */
interface ClassroomRelation {
  getId(): string;
  getAssignmentRelationships(id: string): AssignmentRelation | undefined;
}

/** Subset of a student relationship model reached via `getStudents()`. */
interface StudentRelation {
  getId(): string;
  // `relationshipMeta` is a dynamic JSON:API meta Map; its `draft_assignment` entry maps
  // draft-assignment ids to assignment-relationship models (each an Immutable Map of settings).
  get(key: 'relationshipMeta'): Map<string, Map<string, Map<string, unknown>>>;
  get(key: string): Map<string, unknown>;
}

/**
 * `this` inside the extension methods is a `DraftAssignmentModelV1` instance augmented via
 * `DraftAssignmentModelV1.extend(draftAssignmentExtensions)`. The generated model extends a dynamic
 * `GenericModel` base (an `.js` source typed as `any`), so the model/relationship getters reached
 * through `this` are not statically visible. We declare the ones used here with honest types; values
 * that are genuinely dynamic JSON:API data are typed `unknown` and narrowed at the use site. The
 * shape is applied as the `this` context via `ThisType`.
 */
interface DraftAssignmentExtensionContext {
  getId(): string;
  getSettings(): Map<string, unknown>;
  getStudents(): List<StudentRelation>;
  getClassrooms(): List<ClassroomRelation>;
  getStudentClassrooms(): List<ClassroomRelation>;
  getClassroomRelationships(classroomId?: string): AssignmentRelation | undefined;
}

const draftAssignmentExtensions = {
  /**
   * @see assignmentExtensions.getStartDateFromSettings
   */
  getStartDateFromSettings: assignmentExtensions.getStartDateFromSettings,
  /**
   * @see assignmentExtensions.getDueDateFromSettings
   */
  getDueDateFromSettings: assignmentExtensions.getDueDateFromSettings,
  /**
   * @see assignmentExtensions.getIsAllowLateSubmissionsFromSettings
   */
  getIsAllowLateSubmissionsFromSettings: assignmentExtensions.getIsAllowLateSubmissionsFromSettings,
  /**
   * @see assignmentExtensions.getIsRandomizeQuestionsFromSettings
   */
  getIsRandomizeQuestionsFromSettings: assignmentExtensions.getIsRandomizeQuestionsFromSettings,
  /**
   * @see assignmentExtensions.getTimeLimitFromSettings
   */
  getTimeLimitFromSettings: assignmentExtensions.getTimeLimitFromSettings,
  /**
   * @see assignmentExtensions.getCorrectAnswerSettingFromSettings
   */
  getCorrectAnswerSettingFromSettings: assignmentExtensions.getCorrectAnswerSettingFromSettings,
  /**
   * @see assignmentExtensions.getMessageFromSettings
   */
  getMessageFromSettings: assignmentExtensions.getMessageFromSettings,
  /**
   * @see assignmentExtensions.getLtiLineItemIdFromSettings
   */
  getLtiLineItemIdFromSettings: assignmentExtensions.getLtiLineItemIdFromSettings,
  /**
   * @see assignmentExtensions.getIsAutoAssignNewClassroomEnrolleesFromSettings
   */
  getIsAutoAssignNewClassroomEnrolleesFromSettings:
    assignmentExtensions.getIsAutoAssignNewClassroomEnrolleesFromSettings,
  /**
   * @see assignmentExtensions.getLockdownModeEnabledFromSettings
   */
  getLockdownModeEnabledFromSettings: assignmentExtensions.getLockdownModeEnabledFromSettings,
  getSettingsFromRelations(idsToSelect: IdsToSelect = {}) {
    const {classroomId, studentId} = idsToSelect;

    // student settings should always be returned first
    if (studentId) {
      const students = this.getStudents();
      const student = students.find((st) => st?.getId() === studentId);
      const studentAssignmentRelationships = student!
        .get('relationshipMeta')
        .get('draft_assignment');
      const studentAssignment = studentAssignmentRelationships!.get(this.getId());
      const settings = studentAssignment?.get('settings');
      if (settings) {
        return settings;
      }
    }
    if (classroomId) {
      const classrooms = this.getClassrooms().isEmpty()
        ? this.getStudentClassrooms()
        : this.getClassrooms();
      const classroom = (classrooms || List()).find((cr) => cr?.getId() === classroomId);
      const classroomAssignment =
        classroom?.getAssignmentRelationships?.(this.getId()) ||
        this.getClassroomRelationships(classroomId);
      const settings = classroomAssignment?.getSettings?.();
      if (settings) {
        return settings;
      }
    }
    return this.getSettings();
  }
} satisfies ThisType<DraftAssignmentExtensionContext>;

export {draftAssignmentExtensions};
